import { NavigationProp } from "@react-navigation/core"
import React, { useState, useEffect } from "react"
import { View, Text } from "react-native"
import { useDevices } from "@hooks"
import { FloatTextInput, InfoList } from "@atomic"
import { Title18, Title13 } from "@stylesheets"
import { Button } from "@modules/profile/screens/SettingsScreen/MfaSetup/Button"
import { SecurityFormStyles } from "./styles"
import {
  EstablishmentSelectionStyles,
  inputContainerStyles,
} from "../EstablishmentSelection/styles"
import { AccountActions, BadgeLayouts, HoldingAdditionErrors } from "@modules"
import { useDispatch, useSelector } from "react-redux"
import { GlobalState } from "@redux"
import { InfoBox } from "@atomic/molecules/InfoBox"
import { I18n } from "react-redux-i18n"
import { Colors } from "@constants"
import useBadgeManagement from "@hooks/useBadgeManagement"

type SecurityFormProps = {
  navigation: NavigationProp<any>;
  layoutHandler?: (event: any, key: BadgeLayouts) => void;
  setPreferredZoneData: any;
  preferredZoneData: {
    selectedPrefZones: any[];
    hasPrefZonesError: boolean;
  };
  holdingViewAlias: string;
}

export const SecurityForm = React.forwardRef<View, SecurityFormProps>(
  ({ navigation, layoutHandler, setPreferredZoneData, preferredZoneData, holdingViewAlias }, ref) => {
    const [isMobile] = useDevices()

    const [securityCode, setSecurityCode] = useState<string>("")
    const [showError, setShowError] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const styles = SecurityFormStyles(isMobile)
    const stylesStep1 = EstablishmentSelectionStyles(isMobile)

    const {selectedHolding, isEditionMode, editionHoldingView} = useSelector(
      (state: GlobalState) => state.account
    )

    const dispatch = useDispatch();

    useEffect(() => {
      if (
        isEditionMode &&
        editionHoldingView &&
        editionHoldingView?.holding?.id === selectedHolding?.id
      ) {
        setPreferredZoneData({
          hasPrefZonesError: editionHoldingView?.preferredZones?.length === 0,
          selectedPrefZones: editionHoldingView?.preferredZones || [],
        })

      }
    }, [isEditionMode, editionHoldingView])

    const { sendBadge } = useBadgeManagement()

    const onChangeSecurityCode = (input: string) => {
      setSecurityCode(input)
    }

    const onSecondaryPress = async () => {
      await dispatch(AccountActions.setHasBadge(true))
    }

    const checkPrefZones =
      preferredZoneData.hasPrefZonesError &&
      selectedHolding?.enabledPreferredZones;

    const isActivateButtonDisabled = securityCode.trim().length === 0 || checkPrefZones;

    const onServicesActivation = async () => {
      setShowError(false)
      setErrorMessage("")
      try {
        await sendBadge(
          {
            idHolding: selectedHolding!.id,
            holdingSecurityCode: securityCode.trim(),
            alias: holdingViewAlias,
            serialNumber: "",
            guestLastName: "",
            guestValidationCode: "",
            preferredZones: selectedHolding?.enabledPreferredZones
              ? preferredZoneData.selectedPrefZones.map((zone) => ({
                  id: String(zone.id) as string,
                  name: zone.name as string,
                }))
              : undefined,
          },
          BadgeLayouts.SecurityForm
        )
      } catch (error) {
        setShowError(true)

        if (error === HoldingAdditionErrors.SECURITY_CODE_INVALID) {
          setErrorMessage(
            I18n.t("associationBadge.securityForm.invalidEstablishmentCode")
          )
          return
        }
        setErrorMessage(I18n.t("associationBadge.globalError"))
      }
    }

    const infoListItems = [
      I18n.t(
        "associationBadge.securityForm.infoEstablishmentCodeDescription.0"
      ),
      I18n.t(
        "associationBadge.securityForm.infoEstablishmentCodeDescription.1"
      ),
    ];

    return (
      <>
        <View
          onLayout={(e) => layoutHandler?.(e, BadgeLayouts.SecurityForm)}
          ref={ref}
          style={styles.container}
        >
          <View style={stylesStep1.listContainer}>
            <Title18 isBold style={styles.titleText}>
              {I18n.t("associationBadge.securityForm.establishmentCodeLabel")}
            </Title18>
            <Title13 style={stylesStep1.subtitleText}>
              {I18n.t(
                "associationBadge.securityForm.establishmentCodeSubtitle"
              )}
            </Title13>

            <View style={styles.textHoldingInputContainer}>
              <FloatTextInput
                value={securityCode}
                disabled={false}
                onChangeValue={onChangeSecurityCode}
                hasError={showError}
                errorMessage={errorMessage}
                textPlaceHolder='Ex: 32782'
                containerStyles={inputContainerStyles}
              />
            </View>
          </View>
          <InfoBox
            type='info'
            title={
              <Text style={stylesStep1.infoBoxTitle}>
                {I18n.t(
                  `associationBadge.securityForm.infoEstablishmentCodeTitle`
                )}
              </Text>
            }
            collapsible
            defaultOpen={false}
          >
            <InfoList items={infoListItems} bulletColor={Colors.darkGrey} />
            <Text style={styles.infoBoxHelpStatement}>
              {I18n.t("associationBadge.securityForm.helpStatement")}
            </Text>
          </InfoBox>
          <View style={styles.buttonsContainer}>
            <Button type='secondary' onPress={onSecondaryPress}>
              <Text>{I18n.t("associationBadge.securityForm.haveBadge")}</Text>
            </Button>
            <Button
              disabled={isActivateButtonDisabled}
              type='primary'
              onPress={onServicesActivation}
            >
              <Text>
                {isEditionMode ? I18n.t(`associationBadge.securityForm.saveModifications`) : I18n.t("associationBadge.securityForm.activateServices")}
              </Text>
            </Button>
          </View>
        </View>
      </>
    )
  }
)
